import { Colleague } from "../../../../features/Connections/types/Colleague.type";
import { FleetManagementRow, VehicleBookingEventDto } from "../../types/FleetManagement-row.types";
import { VehicleBlockingEvent } from "../../types/FleetManagement.types";
import { FleetEventType, type FleetEventRow } from "../FleetManagementEventDetailTable.component";

export function buildFleetEventData(
  users: Colleague[] | undefined,
  allFleetData: FleetManagementRow[] | undefined,
  selectedRow: FleetManagementRow | null,
  bookingEvents: VehicleBookingEventDto[] | undefined,
  blockingEvents: VehicleBlockingEvent[] | undefined
): FleetEventRow[] {
  const bookingToEventRow = (
    booking: VehicleBookingEventDto,
    index: number,
    rowId: number,
    odometer?: number | null
  ): FleetEventRow => {
    const bookedForId =
      (booking.userBookedFor !== undefined && booking.userBookedFor !== ""
        ? booking.userBookedFor
        : booking.userInfo?.userId) ?? undefined;
    const bookedById =
      booking.userBookedBy !== undefined && booking.userBookedBy !== ""
        ? booking.userBookedBy
        : undefined;
    const matchedUser = bookedForId
      ? users?.find(u => u.userId === bookedForId) ?? null
      : users?.find(u => u.userId === booking.userInfo?.userId) ?? null;
    const matchedBookedBy =
      bookedById && bookedById !== bookedForId
        ? users?.find(u => u.userId === bookedById) ?? null
        : null;
    return {
      id: `booking-${rowId}-${index}`,
      eventType: FleetEventType.BOOKING,
      start: booking.startTime,
      end: booking.endTime,
      user: matchedUser ?? null,
      userBookedBy: matchedBookedBy ?? null,
      bookingId: booking.bookingId,
      isCheckedIn: booking.isCheckedIn ?? false,
      isCheckedOut: booking.isCheckedOut ?? false,
      location: booking.location ? { id: booking.location.id, name: booking.location.name } : null,
      reason: booking.reason ?? "",
      vehicleId: rowId,
      odometer
    };
  };

  const bookingRowsFromApi = (
    bookingEventsList: VehicleBookingEventDto[],
    vehicleId: number,
    odometer?: number | null
  ): FleetEventRow[] =>
    bookingEventsList.map((booking, index) =>
      bookingToEventRow(booking, index, vehicleId, odometer)
    );

  const blockRowsFromApi = (blockingEventsList: VehicleBlockingEvent[]): FleetEventRow[] =>
    blockingEventsList.map(be => {
      const createdByUser = be.createdBy
        ? users?.find(u => u.userId === be.createdBy) ?? null
        : null;
      return {
        id: `blocking-${be.id}`,
        eventType: FleetEventType.BLOCKING,
        start: be.start,
        end: be.end,
        reason: be.reason ?? "",
        blockingEventId: be.id,
        vehicleId: be.vehicleInventoryId,
        user: createdByUser
      };
    });

  const hasAllFleetData = Boolean(allFleetData && allFleetData.length > 0);
  if (hasAllFleetData) {
    const events: FleetEventRow[] = [];
    // When bookingEvents are provided (calendar mode), group them by vehicle and prefer them
    // over row.occupiedBooking; otherwise fall back to the data from the fleet list.
    const bookingsByVehicle = new Map<number, VehicleBookingEventDto[]>();
    if (bookingEvents?.length) {
      bookingEvents.forEach(be => {
        const id = be.vehicleInventoryId;
        if (id == null) return;
        const list = bookingsByVehicle.get(id) ?? [];
        list.push(be);
        bookingsByVehicle.set(id, list);
      });
    }

    allFleetData!.forEach(row => {
      const apiBookings = bookingsByVehicle.get(row.id);
      if (apiBookings && apiBookings.length > 0) {
        events.push(...bookingRowsFromApi(apiBookings, row.id, row.odometer ?? null));
      } else if (row.occupiedBooking?.length) {
        events.push(...bookingRowsFromApi(row.occupiedBooking, row.id, row.odometer ?? null));
      }
    });

    if (blockingEvents?.length) {
      events.push(...blockRowsFromApi(blockingEvents));
    } else {
      allFleetData!.forEach(row => {
        row.blockedEvents?.forEach((be, index) => {
          events.push({
            id: `blocking-${row.id}-${index}`,
            eventType: FleetEventType.BLOCKING,
            start: be.start,
            end: be.end,
            reason: be.reason ?? "",
            vehicleId: row.id,
            user: null
          });
        });
      });
    }
    return events;
  }

  if (!selectedRow) {
    return [];
  }

  const events: FleetEventRow[] = [];
  if (bookingEvents && bookingEvents.length > 0) {
    events.push(...bookingRowsFromApi(bookingEvents, selectedRow.id, selectedRow.odometer ?? null));
  } else if (selectedRow.occupiedBooking?.length) {
    events.push(
      ...bookingRowsFromApi(
        selectedRow.occupiedBooking,
        selectedRow.id,
        selectedRow.odometer ?? null
      )
    );
  }

  if (blockingEvents && blockingEvents.length > 0) {
    events.push(...blockRowsFromApi(blockingEvents));
  } else {
    selectedRow.blockedEvents?.forEach((be, index) => {
      events.push({
        id: `blocking-${selectedRow.id}-${index}`,
        eventType: FleetEventType.BLOCKING,
        start: be.start,
        end: be.end,
        reason: be.reason ?? "",
        vehicleId: selectedRow.id,
        user: null
      });
    });
  }
  return events;
}
